import { makeAutoObservable } from 'mobx'

import { getApiErrorMessage } from 'shared/lib/errorText'

import { fetchObjectBookingsPage } from './fetch-object-bookings-page'
import type { ObjectBooking } from './types'

const OBJECT_BOOKINGS_PAGE_SIZE = 25

class KyrgyzstanObjectBookingsStore {
  bookings: ObjectBooking[] = []

  isLoading = false

  hasLoaded = false

  error: string | null = null

  page = 1

  totalPages = 1

  activeOnly = true

  abortController: AbortController | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get hasBookings() {
    return this.bookings.length > 0
  }

  get hasMore() {
    return this.page < this.totalPages
  }

  get shouldShowInitialLoader() {
    return (!this.hasLoaded || this.isLoading) && !this.hasBookings
  }

  get shouldShowPaginationLoader() {
    return this.isLoading && this.hasBookings
  }

  get shouldShowEmptyState() {
    return (
      this.hasLoaded &&
      !this.isLoading &&
      (Boolean(this.error) || !this.hasBookings)
    )
  }

  fetchBookings = async (activeOnly = true, nextPage = 1) => {
    const isFirstPage = nextPage === 1

    if (isFirstPage) {
      this.abortController?.abort()
      this.bookings = []
      this.page = 1
      this.totalPages = 1
      this.hasLoaded = false
      this.activeOnly = activeOnly
    }

    const abortController = new AbortController()
    this.abortController = abortController

    this.isLoading = true
    this.error = null

    try {
      const result = await fetchObjectBookingsPage({
        activeOnly,
        page: nextPage,
        pageSize: OBJECT_BOOKINGS_PAGE_SIZE,
        signal: abortController.signal,
      })

      this.bookings = isFirstPage
        ? result.items
        : [...this.bookings, ...result.items]
      this.page = result.page
      this.totalPages = result.totalPages
    } catch (requestError) {
      const errorMessage = getApiErrorMessage(requestError)

      if (errorMessage === null || abortController.signal.aborted) {
        return
      }

      this.error = errorMessage

      if (isFirstPage) {
        this.bookings = []
      }
    } finally {
      if (!abortController.signal.aborted) {
        this.isLoading = false
        this.hasLoaded = true
      }
    }
  }

  refetchBookings = () => {
    return this.fetchBookings(true)
  }

  loadMoreBookings = async () => {
    if (this.isLoading || !this.hasMore) {
      return
    }

    await this.fetchBookings(this.activeOnly, this.page + 1)
  }

  clearBookings = () => {
    this.abortController?.abort()
    this.abortController = null
    this.bookings = []
    this.isLoading = false
    this.hasLoaded = false
    this.error = null
    this.page = 1
    this.totalPages = 1
    this.activeOnly = true
  }
}

export const kyrgyzstanObjectBookingsStore = new KyrgyzstanObjectBookingsStore()
